
export interface Clue {
    x: number,
    y: number,
    value: number
}

export type Puzzle = {
    width: number,
    height: number,
    clues: Clue[],
}

export type Vertex = {
    x: number,
    y: number
}

export type Location = {
    col: number,
    row: number
}

export type Slant = Location & {
    value: number
}

export type Edge = [Vertex, Vertex]

export type Slants = number[][];

export function edgeIncludesVertex(edge: Edge, vertex: Vertex): boolean {
    for (const v of edge) {
        if (v.x == vertex.x && v.y == vertex.y) return true;
    }
    return false;
}

export function slantEdge(slant: Slant): Edge {
    let edge: Edge = null;
    if (!slant) return null;

    let { row, col } = slant;
    if (slant.value == 1) edge = [{ x: col, y: row + 1 }, { x: col + 1, y: row }];
    if (slant.value == 2) edge = [{ x: col, y: row }, { x: col + 1, y: row + 1 }];
    return edge;
}

function vertexEqual(v1: Vertex, v2: Vertex) {
    return (v1.x == v2.x) && (v1.y == v2.y);
}

export function locationEqual(l1: Location, l2: Location) {
    return (l1.col == l2.col) && (l1.row == l2.row);
}

function reachableFromEdge(edge: Edge, v: Vertex) {
    if (edge == null) return null;
    if (vertexEqual(edge[0], v)) return edge[1];
    if (vertexEqual(edge[1], v)) return edge[0];
    return null;
}


export class PuzzleAttempt {
    puzzle: Puzzle;
    slants: number[][];

    constructor(puzzle: Puzzle, cloneFrom: { slants: number[][] } = null) {
        this.puzzle = puzzle;
        this.slants = new Array(puzzle.width);
        for (let i = 0; i < puzzle.width; i++) {
            this.slants[i] = cloneFrom ? cloneFrom.slants[i].slice() : new Array(puzzle.height).fill(0);
        }
    }

    clone(): PuzzleAttempt {
        let clone = new PuzzleAttempt(this.puzzle, this);
        return clone;
    }

    setSlant(loc: Location, value: number) {
        this.slants[loc.col][loc.row] = value;
    }

    toggleSlant(loc: Location) {
        let oldval = this.getSlant(loc).value;
        this.setSlant(loc, ((oldval + 1) % 3));
    }

    getSlant(loc: Location): Slant {
        let { col, row } = loc;
        let slant = this.slants[col][row];

        return { col: col, row: row, value: slant }
    }

    slantsSurroundingVertex(vertex: Vertex): Slant[] {
        let { x, y } = vertex;
        let width = this.puzzle.width, height = this.puzzle.height;
        let slants: Slant[] = []

        if (x < width && y < height) slants.push(this.getSlant({ col: x, row: y }))
        if (x < width && y > 0) slants.push(this.getSlant({ col: x, row: y - 1 }))
        if (x > 0 && y < height) slants.push(this.getSlant({ col: x - 1, row: y }))
        if (y > 0 && x > 0) slants.push(this.getSlant({ col: x - 1, row: y - 1 }))

        return slants;
    }

    slantsIntersectingVertex(vertex: Vertex): Slant[] {
        let slants = this.slantsSurroundingVertex(vertex);
        return slants.filter(s => {
            let e = slantEdge(s);
            return e && edgeIncludesVertex(e, vertex)
        });
    }

    edgesForVertex(vertex: Vertex): Edge[] {
        let slants = this.slantsSurroundingVertex(vertex);
        return slants.map(s => slantEdge(s)).filter(e => e != null && edgeIncludesVertex(e, vertex))
    }

    *eachVertex(): Generator<Vertex> {
        for (let x = 0; x <= this.puzzle.width; x++) {
            for (let y = 0; y <= this.puzzle.height; y++) {
                yield { x: x, y: y }
            }
        }
    }

    *eachSlantLocation(): Generator<Location> {
        for (let x = 0; x < this.puzzle.width; x++) {
            for (let y = 0; y < this.puzzle.height; y++) {
                yield { col: x, row: y }
            }
        }
    }

    getCycles(vertex: Vertex = null): Slant[] {

        type HistoryEntry = { parent: HistoryEntry, vertex: Vertex, slant: Slant, isCycle?: boolean }

        const explored: HistoryEntry[][] = new Array(this.puzzle.width + 1);
        for (var i = 0; i < explored.length; i++) {
            explored[i] = new Array(this.puzzle.height + 1).fill(null);
        }

        const cycleSlants: Set<Slant> = new Set();

        const traverse = (path: HistoryEntry, vertex: Vertex) => {

            // have we made a cycle? 
            let visited = explored[vertex.x][vertex.y];
            if (visited) {
                let left = path;
                let right = visited;

                let leftStack:HistoryEntry[] = []
                let rightStack:HistoryEntry[] = []

                //build a node list
                while (left) { leftStack.push(left), left = left.parent}
                while (right) { rightStack.push(right), right = right.parent}
                
                //discard the common base
                while (leftStack && rightStack && leftStack[leftStack.length-1] == rightStack[rightStack.length-1]) { 
                    leftStack.pop();
                    rightStack.pop();
                }
                
                //add the cycle slants
                for(let s of leftStack) {
                    if (s.slant) cycleSlants.add(s.slant)
                }
                for(let s of rightStack) {
                    if (s.slant) cycleSlants.add(s.slant)
                }

                return;
            }

            explored[vertex.x][vertex.y] = path ?? { slant: null, vertex: vertex, parent: path }

            // if not, keep exploring
            let candidateSlants = this.slantsSurroundingVertex(vertex);
            for (let s of candidateSlants) {
                // don't back track
                if (path && path.slant.col == s.col && path.slant.row == s.row) continue;

                let dest = reachableFromEdge(slantEdge(s), vertex);
                if (dest) {
                    traverse({ slant: s, vertex: dest, parent: path }, dest);
                }
            }
        }

        let scope = vertex ? [vertex] : this.eachVertex();
        for (let v of scope) {
            if (!explored[v.x][v.y]) traverse(null, v);
        }

        return [...cycleSlants.values()];
    }

    isValidSlant(loc: Location): boolean {
        // check the surrounding clues to ensure we haven't violated anything
        let {row, col} = loc;
        let verticies=[{x: col, y: row},{x: col+1, y: row}, {x: col+1, y: row+1}, {x: col, y: row+1}];
        for (let v of verticies) {
            let clue = this.puzzle.clues.find(x => vertexEqual(x, v));
            if (!clue) continue;
            if (!this.clueIsValid(clue)) return false;
        }

        //check vertex on the slant to see if it is in any cycles
        let s = this.getSlant(loc);
        let e = slantEdge(s);
        let cycles = this.getCycles(e[0]);
        return cycles.length == 0
    }


    clueIsValid(c: Clue): boolean {
        let slants = this.slantsSurroundingVertex(c);
        let slantsIntersectingVertex = this.slantsIntersectingVertex(c);
        let emptySlants = slants.filter(s => s.value == 0);

        let clueNeeded = c.value - slantsIntersectingVertex.length;

        if (clueNeeded < 0) return false;
        if (clueNeeded > emptySlants.length) return false;
        return true;
    }


    isValid(): boolean {
        //clues are valid
        for (const c of this.puzzle.clues) {
            if (!this.clueIsValid(c)) return false;
        }

        return this.getCycles().length == 0;
    }

    allBlanksHaveSolution(): boolean {
        for (let loc of this.eachSlantLocation()) {
            let s = this.getSlant(loc);
            if (s.value != 0) continue;
            this.setSlant(loc, 1);
            let OneIsValid = this.isValid();
            this.setSlant(loc, 2);
            let TwoIsValid = this.isValid();
            this.setSlant(loc, 0);
            return OneIsValid || TwoIsValid;
        }
    }

    isComplete(): boolean {
        for (let s of this.eachSlantLocation()) {
            if (this.getSlant(s).value == 0) return false;
        }
        return true;
    }

}




export function genStaticPuzzle(width, height): Puzzle {

    return {
        width, height, clues: [
            { x: 1, y: 0, value: 1 },
            { x: 2, y: 0, value: 1 },
            { x: 4, y: 0, value: 1 },

            { x: 0, y: 1, value: 1 },
            { x: 1, y: 1, value: 2 },
            { x: 4, y: 1, value: 1 },
            { x: 5, y: 1, value: 1 },

            { x: 1, y: 2, value: 1 },
            { x: 2, y: 2, value: 3 },
            { x: 4, y: 2, value: 3 },

            { x: 2, y: 3, value: 3 },
            { x: 4, y: 3, value: 2 },

            { x: 1, y: 4, value: 1 },
            { x: 3, y: 4, value: 3 },
            { x: 4, y: 4, value: 1 },

            { x: 2, y: 5, value: 1 },
        ]
    }
}
